import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Search, CalendarClock, AlertTriangle, CheckCircle2, X, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clearAuthAndTenant, PRODUCT_NAME } from '../lib/tenant';
import { authApi } from '../api';
import { useUnifiedCustomers } from '../lib/workspaceStore';
import { useCompanyProfile, companyDisplayName } from '../lib/companyProfile';
import { mediaSrc } from '../lib/mediaUrl';
import {
  getActiveLedger,
  getLedgerList,
} from '../lib/ledger';
import { getCurrentUser } from '../lib/permissions';
import {
  normalizeWorkingDate,
  readWorkspaceAsOfDate,
  setWorkspaceAsOfDate,
  todayYmd,
  WORKSPACE_AS_OF_EVENT,
} from '../lib/workspaceAsOf';
import { daysBetweenDueAndPaid } from '../lib/overdue';
import { useTranslation } from '../lib/i18n';
import { ThemeToggle } from '../components/common/ThemeToggle';

type WorkspaceNotification = {
  id: string;
  title: string;
  detail: string;
  overdue: boolean;
};

const inr = (n: number) => `₹${Math.round(Math.max(0, Number(n) || 0)).toLocaleString('en-IN')}`;

function dueYmd(value: string): string {
  if (!value || value === '—') return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const sectionTitleKeys: Record<string, string> = {
  master: 'nav.master',
  dashboard: 'nav.dashboard',
  customers: 'nav.customers',
  finance: 'nav.finance',
  loans: 'nav.loans',
  accounts: 'nav.accounts',
  report: 'nav.report',
  audit: 'nav.audit',
  erp: 'nav.erp',
  consultancy: 'nav.consultancy',
  expenses: 'nav.expenses',
  employees: 'nav.employees',
  settings: 'nav.settings',
};

function workspaceAccountLabel(t: (key: string) => string): string {
  const e = (typeof localStorage !== 'undefined' && localStorage.getItem('userEmail')) || '';
  const local = e.trim();
  if (!local) return t('navbar.account');
  const localPart = local.split('@')[0];
  return localPart.length > 14 ? `${localPart.slice(0, 14)}…` : localPart;
}

interface NavbarProps {
  children?: ReactNode;
  activeItem: string;
  setActiveItem: (item: string) => void;
  /** Open the Customers view focused on a specific borrower (used by notifications). */
  onOpenCustomer?: (id: string) => void;
}

export function Navbar({ children, activeItem, setActiveItem, onOpenCustomer }: NavbarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const { t } = useTranslation();
  const [ledgerList, setLedgerListState] = useState(getLedgerList);
  const [workingDate, setWorkingDate] = useState(() => {
    const saved = readWorkspaceAsOfDate();
    return normalizeWorkingDate(saved || todayYmd());
  });
  const customers = useUnifiedCustomers();
  const { profile: company } = useCompanyProfile();
  const orgName = companyDisplayName(company) || PRODUCT_NAME;
  const navigate = useNavigate();
  const activeLedger = getActiveLedger();
  const isLedgerLogin = getCurrentUser()?.role === 'LEDGER';
  const ledgerLabel = activeLedger?.name || ledgerList.find((l) => l.isDefault)?.name || 'Ledger';
  const wallToday = todayYmd();
  const isTestDate = workingDate !== wallToday;

  useEffect(() => {
    // Default books date to today when unset.
    const saved = readWorkspaceAsOfDate();
    const next = normalizeWorkingDate(saved || todayYmd());
    if (saved !== next) setWorkspaceAsOfDate(next);
    setWorkingDate(next);

    const syncLedgers = () => {
      setLedgerListState(getLedgerList());
    };
    const syncAsOf = () => setWorkingDate(normalizeWorkingDate(readWorkspaceAsOfDate() || todayYmd()));
    syncLedgers();
    window.addEventListener('workspace-data', syncLedgers);
    window.addEventListener('storage', syncLedgers);
    window.addEventListener(WORKSPACE_AS_OF_EVENT, syncAsOf);
    window.addEventListener('storage', syncAsOf);
    return () => {
      window.removeEventListener('workspace-data', syncLedgers);
      window.removeEventListener('storage', syncLedgers);
      window.removeEventListener(WORKSPACE_AS_OF_EVENT, syncAsOf);
      window.removeEventListener('storage', syncAsOf);
    };
  }, []);

  const onWorkingDateChange = (ymd: string) => {
    const next = normalizeWorkingDate(ymd || todayYmd());
    setWorkspaceAsOfDate(next);
    setWorkingDate(next);
    window.dispatchEvent(new Event('workspace-data'));
  };

  const resetWorkingDate = () => {
    setWorkspaceAsOfDate(null);
    const next = todayYmd();
    setWorkingDate(next);
    window.dispatchEvent(new Event('workspace-data'));
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Only surface *late* (overdue) borrowers — same day math as customer overview.
  const notifications = useMemo<WorkspaceNotification[]>(() => {
    const books = normalizeWorkingDate(workingDate || todayYmd());
    const items: (WorkspaceNotification & { sortKey: string })[] = [];
    for (const c of customers) {
      const due = dueYmd(c.pendingEmi?.dueDate ?? '');
      const amount = c.pendingEmi?.amount ?? 0;
      if (!due || amount <= 0) continue;
      const daysLate = daysBetweenDueAndPaid(due, books);
      if (daysLate <= 0) continue;
      const dueLabel = new Date(`${due}T12:00:00`).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
      });
      items.push({
        id: c.id,
        title: c.name || t('common.borrower'),
        detail: `${daysLate} day${daysLate === 1 ? '' : 's'} overdue · ${inr(amount)} · due ${dueLabel}`,
        overdue: true,
        sortKey: due,
      });
    }
    return items
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .slice(0, 12)
      .map(({ sortKey: _s, ...rest }) => rest);
  }, [customers, t, workingDate]);

  const overdueCount = notifications.length;

  useEffect(() => {
    if (!isNotifOpen && !isDateOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isNotifOpen, isDateOpen]);

  const openCustomer = (id: string) => {
    if (onOpenCustomer) onOpenCustomer(id);
    else setActiveItem('customers');
    setIsNotifOpen(false);
  };

  const handleLogout = () => {
    authApi.logout().catch(() => {});
    clearAuthAndTenant();
    navigate('/login');
  };

  const title = sectionTitleKeys[activeItem] ? t(sectionTitleKeys[activeItem]) : t('nav.dashboard');

  return (
    <header className="sticky top-0 z-20 border-b border-zinc-200 bg-white pt-[var(--safe-area-inset-top,env(safe-area-inset-top,0px))] dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex h-14 items-center gap-3 px-[max(0.75rem,var(--safe-area-inset-left,env(safe-area-inset-left,0px)))] pr-[max(0.75rem,var(--safe-area-inset-right,env(safe-area-inset-right,0px)))] sm:px-5 lg:px-6">
        {children}

        <div className="min-w-0 flex-1">
          <p className="truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-800 dark:text-emerald-400">{orgName}</p>
          <h1 className="truncate text-sm font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-base">{title}</h1>
        </div>

        <div className="relative hidden max-w-md flex-1 md:block lg:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
          <input
            type="search"
            placeholder={t('common.searchWorkspace')}
            className="h-9 w-full rounded-md border border-zinc-200 bg-zinc-50 py-0 pl-8 pr-2 text-sm text-zinc-900 placeholder:text-zinc-400 transition-colors focus:border-emerald-500/70 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500/25 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:bg-zinc-800"
          />
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3">
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 sm:hidden"
            aria-label="Working date"
            title={workingDate ? `Working date ${workingDate}` : 'Working date'}
            onClick={() => {
              setIsNotifOpen(false);
              setIsDropdownOpen(false);
              setIsDateOpen(true);
            }}
          >
            <CalendarClock className="h-4 w-4" />
          </button>
          <label
            className="hidden items-center gap-1.5 sm:inline-flex"
            title="Working date — due, overdue, accruals, and date filters follow this (past or future for testing)"
          >
            <CalendarClock
              className={`h-3.5 w-3.5 shrink-0 ${
                isTestDate ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-300'
              }`}
              aria-hidden
            />
            <span className="sr-only">Working date</span>
            <input
              type="date"
              className={`h-9 max-w-[10.5rem] min-w-0 rounded-md border px-2 text-xs font-medium focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 ${
                isTestDate
                  ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100'
              }`}
              value={workingDate}
              onChange={(e) => onWorkingDateChange(e.target.value)}
            />
            {isTestDate ? (
              <button
                type="button"
                onClick={resetWorkingDate}
                className="rounded border border-zinc-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
                title="Reset to today"
              >
                Today
              </button>
            ) : null}
          </label>

          {ledgerLabel ? (
            <span
              className="hidden max-w-[11rem] items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs font-medium text-emerald-900 sm:inline-flex"
              title={
                isLedgerLogin
                  ? 'Signed in to this ledger only — log in with another ledger account to switch'
                  : 'Active ledger — switch by signing in with that ledger account'
              }
            >
              <BookOpen className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{ledgerLabel}</span>
            </span>
          ) : null}

          <div className="relative">
            <button
              type="button"
              className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 transition-colors hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:border-zinc-500 dark:hover:bg-zinc-700"
              aria-label={t('navbar.notifications')}
              aria-expanded={isNotifOpen}
              onClick={() => {
                setIsDateOpen(false);
                setIsNotifOpen((v) => !v);
                setIsDropdownOpen(false);
              }}
            >
              <Bell className="h-[18px] w-[18px]" />
              {notifications.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-rose-500 px-1 text-[9px] font-bold leading-none text-white">
                  {notifications.length}
                </span>
              )}
            </button>
            {isNotifOpen && typeof document !== 'undefined'
              ? createPortal(
                  <div
                    className="fixed inset-0 z-[120] flex items-center justify-center bg-zinc-950/50 p-4"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setIsNotifOpen(false)}
                  >
                    <div
                      className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-100 bg-gradient-to-r from-rose-50/80 to-white px-4 py-3 dark:border-zinc-700 dark:from-rose-950/40 dark:to-zinc-900">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400">
                            <AlertTriangle className="h-4 w-4" />
                          </span>
                          <div className="leading-tight">
                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('navbar.latePayments')}</p>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{t('common.borrower')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {overdueCount > 0 && (
                            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700 dark:bg-rose-950/50 dark:text-rose-400">
                              {overdueCount}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsNotifOpen(false)}
                            className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                            aria-label={t('common.close')}
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                          <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{t('navbar.noLatePayments')}</p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">Every borrower is on schedule right now.</p>
                        </div>
                      ) : (
                        <ul className="flex-1 divide-y divide-zinc-100 overflow-y-auto dark:divide-zinc-700">
                          {notifications.map((n) => (
                            <li key={n.id}>
                              <button
                                type="button"
                                onClick={() => openCustomer(n.id)}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-rose-50/50 dark:hover:bg-rose-950/30"
                              >
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
                                  <AlertTriangle className="h-4 w-4" />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{n.title}</span>
                                  <span className="block truncate text-xs text-rose-600 dark:text-rose-400">{n.detail}</span>
                                </span>
                                <CalendarClock className="h-4 w-4 shrink-0 text-zinc-300 dark:text-zinc-600" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>,
                  document.body
                )
              : null}
          </div>

          <div className="relative">
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white p-0.5 transition-colors hover:border-emerald-300 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:hover:border-emerald-700 dark:hover:bg-zinc-700 sm:py-0.5 sm:pl-0.5 sm:pr-2.5"
              onClick={toggleDropdown}
              aria-expanded={isDropdownOpen}
              aria-label={t('navbar.account')}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-zinc-300 bg-zinc-100 shadow-sm">
                {company.logoUrl ? (
                  <img
                    src={mediaSrc(company.logoUrl)}
                    alt={orgName}
                    className="h-full w-full object-cover object-center"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-600/15 to-teal-600/10 text-xs font-bold text-emerald-900">
                    {(orgName || '?').charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
              <span className="hidden max-w-[120px] truncate text-xs font-semibold text-zinc-700 dark:text-zinc-200 lg:inline">
                {workspaceAccountLabel(t)}
              </span>
            </button>
            {isDropdownOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-10 cursor-default bg-zinc-950/20"
                  aria-label={t('common.close')}
                  onClick={() => setIsDropdownOpen(false)}
                />
                <div className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-md border border-zinc-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    onClick={() => {
                      setActiveItem('customers');
                      setIsDropdownOpen(false);
                    }}
                  >
                    {t('nav.customers')}
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    onClick={() => {
                      setActiveItem('settings');
                      setIsDropdownOpen(false);
                    }}
                  >
                    {t('nav.settings')}
                  </button>
                  <ThemeToggle
                    variant="menu"
                    labelLight={t('navbar.lightMode')}
                    labelDark={t('navbar.darkMode')}
                    onToggle={() => setIsDropdownOpen(false)}
                  />
                  <button
                    type="button"
                    className="w-full border-t border-zinc-100 px-4 py-2.5 text-left text-sm font-semibold text-rose-700 hover:bg-rose-50 dark:border-zinc-700 dark:text-rose-400 dark:hover:bg-rose-950/40"
                    onClick={handleLogout}
                  >
                    {t('common.logout')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isDateOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[120]" role="presentation">
              <button
                type="button"
                className="absolute inset-0 bg-zinc-950/40"
                aria-label={t('common.close')}
                onClick={() => setIsDateOpen(false)}
              />
              <div
                className="absolute inset-x-0 bottom-0 rounded-t-2xl border border-zinc-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
                role="dialog"
                aria-modal="true"
                aria-label="Working date"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-300 dark:bg-zinc-600" aria-hidden />
                <div className="mb-3 flex items-center gap-2">
                  <span className="navbar-work-date-sheet-icon flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                    <CalendarClock className="h-4 w-4" strokeWidth={2.25} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Working date</p>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                      The whole app runs on this date — past or future for testing
                    </p>
                  </div>
                </div>
                <input
                  type="date"
                  className="app-field app-field-date w-full text-base"
                  value={workingDate}
                  onChange={(e) => onWorkingDateChange(e.target.value)}
                />
                <div className="mt-3 flex gap-2">
                  {isTestDate ? (
                    <button type="button" className="app-btn-secondary flex-1" onClick={resetWorkingDate}>
                      Reset to today
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`app-btn-primary ${isTestDate ? 'flex-1' : 'w-full'}`}
                    onClick={() => setIsDateOpen(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </header>
  );
}
